import { resolvedCanonical } from 'components/seo/canonical';
import description from 'components/seo/description';
import robots from 'components/seo/robots';
import title from 'components/seo/title';
import { IBlogAuthorFields, IBlogCategoryFields, IBlogPostFields } from 'global';
import { useRegion } from 'hooks/use-region';
import Head from 'next/head';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { jsonLdScriptProps } from 'react-schemaorg';
import { BreadcrumbList } from 'schema-dts';
import { getMagazineUrl } from './util';

type MagazineHeadProps = Pick<
  IBlogCategoryFields,
  'pageTitle' | 'metaDescription' | 'metaRobots'
> & {
  canonicalUrl: string;
  mainCategory?: IBlogCategoryFields;
  subCategory?: IBlogCategoryFields;
  blogPost?: IBlogPostFields;
  author?: IBlogAuthorFields;
};

const MagazineHead: FC<MagazineHeadProps> = ({
  canonicalUrl,
  pageTitle,
  metaDescription,
  metaRobots,
  mainCategory,
  subCategory,
  blogPost,
  author,
}) => {
  const region = useRegion();
  const { formatMessage } = useIntl();
  const descriptionTags = description(metaDescription);
  const breadcrumbs = [
    {
      name: formatMessage({ id: 'magazine:breadcrumb:magazine' }),
      item: getMagazineUrl(undefined, true, region),
    },
  ];

  if (mainCategory) {
    breadcrumbs.push({
      name: mainCategory.pageTitle || mainCategory.name,
      item: getMagazineUrl({ mainCategory }, true, region),
    });
  }
  if (mainCategory && subCategory) {
    breadcrumbs.push({
      name: subCategory.pageTitle || subCategory.name,
      item: getMagazineUrl({ mainCategory, subCategory }, true, region),
    });
  }
  if (mainCategory && subCategory && blogPost) {
    breadcrumbs.push({
      name: blogPost.pageTitle || blogPost.title,
      item: getMagazineUrl({ mainCategory, subCategory, blogPost }, true, region),
    });
  }
  if (author) {
    breadcrumbs.push({
      name: author.name,
      item: getMagazineUrl({ author }, true, region),
    });
  }

  return (
    <Head>
      {pageTitle && title(pageTitle)}
      {metaDescription && descriptionTags}
      {metaRobots && robots(metaRobots)}
      {resolvedCanonical(canonicalUrl)}
      <script
        {...jsonLdScriptProps<BreadcrumbList>({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            ...item,
          })),
        })}
      />
    </Head>
  );
};

export default MagazineHead;
